import {createReducer} from '@reduxjs/toolkit';

import {adapter, initialState} from './state';
import {bookingCancelAction, bookingCreateAction, bookingDeleteAction, bookingMoveAction, bookingUncancelAction, clearAllAction, clearErrorsAction, getAllBookings,} from './actions';
import {ApiResponse, Booking} from "../../models";

export const reducer = createReducer(initialState, builder =>
    builder
        /** HANDLE ACTION ====================================== */
        .addCase(getAllBookings.pending, state => {
            return {...state, isFetching: true, errorMessage: undefined};
        })
        .addCase(getAllBookings.fulfilled, (state, action) => {
            // The service puts the scope ids at the payload TOP LEVEL, not under
            // `.results` (which only carries `bookings`). Reading them from the
            // right place is what makes the stale-removal branch live at all.
            const {learnerId, customerId, results} =
                action.payload as ApiResponse & {learnerId?: number; customerId?: number};
            // The API serialises a non-empty list as an id-keyed OBJECT
            // (keyBy('id') server-side); only an empty result is a real array.
            // Object.values normalises both shapes to Booking[].
            const bookings: Booking[] = Object.values(results?.bookings ?? {});

            // A scoped refetch is authoritative for the owners it covers, so any
            // stored booking for those owners that the server no longer returns
            // must be pruned (a staff/other-device cancellation). Owners are keyed
            // by learnerId — bookings carry no customerId — so a customer-scoped
            // refetch is authoritative for every learner present in its response.
            const returnedBookingIds = new Set(bookings.map(b => b.id));
            const authoritativeLearnerIds = new Set(
                bookings.map(b => b.learnerId).filter((id): id is number => id != null)
            );
            if (learnerId != null) {
                authoritativeLearnerIds.add(learnerId);
            }

            if (learnerId != null || customerId != null) {
                const staleIds = state.ids.filter(id => {
                    const booking = state.entities[id];
                    // A freshly created booking carries swimmer_id (→ swimmerId), not
                    // learner_id, so its learnerId is undefined; it belongs to no
                    // authoritative owner here and must NOT be pruned.
                    return booking?.learnerId != null &&
                        authoritativeLearnerIds.has(booking.learnerId) &&
                        !returnedBookingIds.has(id);
                });
                adapter.removeMany(state, staleIds);
            }

            adapter.upsertMany(state, bookings);
            state.isFetching = false;
        })
        .addCase(getAllBookings.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isFetching: false, errorMessage};
        })

        /** HANDLE ACTION ====================================== */
        .addCase(bookingCreateAction.pending, state => {
            return {...state, isChangingBooking: true, errorMessage: undefined};
        })
        .addCase(bookingCreateAction.fulfilled, (state, action) => {
            const {booking} = action.payload.results;
            if (booking){
                return adapter.upsertOne({...state, isChangingBooking: false}, booking);
            }
            else {
                return {...state, isChangingBooking: false};
            }
        })
        .addCase(bookingCreateAction.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isChangingBooking: false, errorMessage};
        })

        /** HANDLE ACTION ====================================== */
        .addCase(bookingDeleteAction.pending, state => {
            return {...state, isChangingBooking: true, errorMessage: undefined};
        })
        .addCase(bookingDeleteAction.fulfilled, (state, action) => {
            const {bookingId} = action.payload;
            if (bookingId){
                return adapter.removeOne({...state, isChangingBooking: false}, bookingId);
            }
            else {
                return {...state, isChangingBooking: false};
            }
        })
        .addCase(bookingDeleteAction.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isChangingBooking: false, errorMessage};
        })

        /** HANDLE ACTION ====================================== */
        // Cancel-attendance mirrors delete's busy/error shape, but keeps the
        // booking entity: it cancels a single date, not the recurring booking,
        // and the fulfilled payload carries only a message (no bookingId). The
        // busy flag guards against double-tap; the rejected errorMessage is what
        // the container toasts — without these cases a failed cancel is silent.
        .addCase(bookingCancelAction.pending, state => {
            return {...state, isChangingBooking: true, errorMessage: undefined};
        })
        .addCase(bookingCancelAction.fulfilled, state => {
            return {...state, isChangingBooking: false};
        })
        .addCase(bookingCancelAction.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isChangingBooking: false, errorMessage};
        })

        /** HANDLE ACTION ====================================== */
        // Uncancel mirrors cancel's busy/error trio exactly — it restores a
        // single cancelled date's attendance, not the recurring booking, so
        // the entity is left in place; the page refetches slots to reconcile
        // the flag flip (canUncancel → canCancel).
        .addCase(bookingUncancelAction.pending, state => {
            return {...state, isChangingBooking: true, errorMessage: undefined};
        })
        .addCase(bookingUncancelAction.fulfilled, state => {
            return {...state, isChangingBooking: false};
        })
        .addCase(bookingUncancelAction.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isChangingBooking: false, errorMessage};
        })

        /** HANDLE ACTION ====================================== */
        // Move mirrors cancel's busy/error shape: it re-points a recurring
        // booking at a new slot, but the page always refetches slots+bookings
        // straight after a successful move, so there is no entity to patch
        // in here — upserting a half-updated booking would just be thrown away.
        .addCase(bookingMoveAction.pending, state => {
            return {...state, isChangingBooking: true, errorMessage: undefined};
        })
        .addCase(bookingMoveAction.fulfilled, state => {
            return {...state, isChangingBooking: false};
        })
        .addCase(bookingMoveAction.rejected, (state, action) => {
            const { errorMessage } = action.payload as ApiResponse;
            return {...state, isChangingBooking: false, errorMessage};
        })

        /** HANDLE NON-API ACTIONS ====================================== */
        .addCase(clearAllAction.fulfilled, () => adapter.removeAll({...initialState}))
        .addCase(clearErrorsAction.fulfilled, state => ({...state, errorMessage: undefined}))
);
